import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdminRole } from '@/hooks/useAdminRole';

/**
 * Single source of truth for whether the astrology feature is visible to the current user.
 * Enabled when:
 *  - user has user_optional_features.astrology_enabled = true
 *  - OR user already has a saved birth_chart (auto-grants access)
 *  - OR user is admin
 */
export function useAstrologyAccess() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();

  const { data, isLoading } = useQuery({
    queryKey: ['astrology-access', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const [{ data: feat }, { data: charts }] = await Promise.all([
        supabase.from('user_optional_features').select('astrology_enabled').eq('user_id', user!.id).maybeSingle(),
        supabase.from('birth_charts').select('id').eq('user_id', user!.id).limit(1),
      ]);
      return {
        flagEnabled: !!feat?.astrology_enabled,
        hasChart: (charts?.length ?? 0) > 0,
      };
    },
  });

  const enabled = isAdmin || !!data?.flagEnabled || !!data?.hasChart;
  return { enabled, loading: authLoading || adminLoading || isLoading, hasChart: !!data?.hasChart };
}

export async function enableAstrologyForUser(userId: string) {
  return supabase.from('user_optional_features').upsert(
    { user_id: userId, astrology_enabled: true, enabled_at: new Date().toISOString() },
    { onConflict: 'user_id' },
  );
}